import { useState } from "react";
import { AlertTriangle, CheckCircle2, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { downloadAnomaliesXlsx } from "@/lib/forensic";

const eur = (v) => `${Math.round(v ?? 0).toLocaleString("fr-FR")} €`;
const n = (v) => Number(v ?? 0).toLocaleString("fr-FR");
export const anomalieKey = (a) => `${a.regle_id}|${a.compte_origine}`;

function csvFallback(rows) {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [["Date", "Compte", "Libellé", "Montant", "Règle déclenchée"].join(";")];
    rows.forEach((a) => (a.exemples || []).forEach((e) => lines.push([e.date, a.compte_origine, e.libelle, ((e.debit || 0) - (e.credit || 0)).toFixed(2), a.regle].map(esc).join(";"))));
    const url = URL.createObjectURL(new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" }));
    const link = Object.assign(document.createElement("a"), { href: url, download: "anomalies_fec_a_reclasser.csv" });
    link.click(); URL.revokeObjectURL(url);
}

/** Tableau des anomalies : case à cocher par ligne (faux positifs décochés = exclus de l'IA et de l'export). */
export default function AnomaliesTable({ scan, dossierId, exclusions = [], onToggle }) {
    const [exporting, setExporting] = useState(false);
    if (!scan) return null;
    const rows = scan.anomalies || [];
    const dirty = rows.length > 0;
    const ex = new Set(exclusions);
    const checked = rows.filter((a) => !ex.has(anomalieKey(a)));

    async function exportXlsx() {
        setExporting(true);
        try { await downloadAnomaliesXlsx(dossierId); toast.success("Export Excel téléchargé", { description: `${checked.length} groupe(s) coché(s)` }); }
        catch (e) { csvFallback(checked); toast.info("Export CSV (repli)", { description: e.message }); }
        finally { setExporting(false); }
    }

    return (
        <div data-testid="anomalies-table" className="space-y-4">
            <div className={`flex flex-wrap items-center gap-3 rounded-md border px-4 py-3 ${dirty ? "border-rose-300 bg-rose-50" : "border-emerald-300 bg-emerald-50"}`}>
                {dirty ? <AlertTriangle className="h-5 w-5 text-rose-700" /> : <CheckCircle2 className="h-5 w-5 text-emerald-700" />}
                <p data-testid="scan-verdict" className={`text-sm font-semibold ${dirty ? "text-rose-800" : "text-emerald-800"}`}>
                    {dirty ? `FEC « sale » : ${n(scan.lignes_suspectes)} écritures suspectes sur ${n(scan.lignes_analysees)} — ${eur(scan.montant_total_erreur)} en jeu` : `FEC propre : aucune anomalie sur ${n(scan.lignes_analysees)} écritures`}
                </p>
                {scan.fichiers?.length > 0 && <span className="ml-auto text-xs text-muted-foreground">{scan.fichiers.map((f) => f.nom).join(" · ")}</span>}
            </div>
            {dirty && (
                <>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <p data-testid="anomalies-checked-count" className="text-xs text-slate-500">{checked.length}/{rows.length} groupe(s) coché(s) → transmis à l&apos;IA et à l&apos;export. Décochez les faux positifs.</p>
                        <Button type="button" variant="outline" size="sm" data-testid="anomalies-export" onClick={exportXlsx} disabled={exporting || !checked.length} className="rounded-full">{exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Exporter pour la Comptabilité (.xlsx)</Button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead><tr className="text-left text-sm font-semibold text-slate-500 border-b border-slate-200"><th className="py-2 pr-2 w-8" /><th className="py-2 pr-3">Règle</th><th className="py-2 pr-3">Compte d'origine</th><th className="py-2 pr-3 text-right">Nb</th><th className="py-2 pr-3 text-right">Montant net</th><th className="py-2 pr-3">Reclassement suggéré</th><th className="py-2">Libellé de l&apos;écriture</th></tr></thead>
                            <tbody className="divide-y divide-border/60">
                                {rows.map((a, i) => {
                                    const on = !ex.has(anomalieKey(a));
                                    return (
                                        <tr key={anomalieKey(a)} data-testid={`anomalie-row-${i}`} className={on ? "" : "opacity-50"}>
                                            <td className="py-2 pr-2"><Checkbox data-testid={`anomalie-check-${i}`} checked={on} onCheckedChange={() => onToggle?.(anomalieKey(a))} className="border-slate-300 data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600" /></td>
                                            <td className="py-2 pr-3 text-rose-800">{a.regle}</td>
                                            <td className="py-2 pr-3 font-mono text-xs">{a.compte_origine}<span className="ml-1 font-sans text-muted-foreground">{a.compte_lib}</span></td>
                                            <td className="py-2 pr-3 text-right tabular-nums">{a.nb_ecritures}</td>
                                            <td className="py-2 pr-3 text-right tabular-nums font-semibold">{eur(a.montant_erreur)}</td>
                                            <td className="py-2 pr-3 text-emerald-800">{a.compte_reclassement}</td>
                                            <td className="py-2 text-sm text-slate-700 max-w-[360px] truncate" title={a.exemples?.[0]?.libelle} data-testid={`anomalie-libelle-${i}`}>{a.exemples?.[0]?.libelle || <span className="text-muted-foreground">—</span>}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}
